import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthSession } from "@/contexts/AuthSessionContext";

type RequireAuthProps = {
  children: React.ReactNode;
  /** When true, we redirect to /auth once (replace) instead of rendering an inline CTA. */
  redirectToAuth?: boolean;
};

export default function RequireAuth({ children, redirectToAuth = false }: RequireAuthProps) {
  const { isAuthed, isLoadingAuth } = useAuthSession();
  const location = useLocation();
  const navigate = useNavigate();
  const didNavigateRef = useRef(false);

  useEffect(() => {
    if (!redirectToAuth) return;
    if (didNavigateRef.current) return;
    if (isLoadingAuth) return;
    if (isAuthed) return;
    if (location.pathname === "/auth") return;

    didNavigateRef.current = true;
    const next = `${location.pathname}${location.search}`;
    navigate(`/auth?next=${encodeURIComponent(next)}`, {
      replace: true,
      state: { from: location.pathname },
    });
  }, [redirectToAuth, isLoadingAuth, isAuthed, location.pathname, navigate]);

  if (isLoadingAuth) {
    return (
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <Skeleton className="h-12 w-48" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthed) {
    // If redirecting, we return a stable placeholder while navigation occurs.
    if (redirectToAuth) {
      return (
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">Redirecting to sign in…</p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <h2 className="text-xl font-bold mb-2">Sign in required</h2>
            <p className="text-muted-foreground mb-4">
              Create an account or sign in to continue.
            </p>
            <Button onClick={() => navigate("/auth", { replace: true })}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
